import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { generateColorPalette, getReadableTextColor } from '../../utils/colorUtils';
import ProfilePreview from '../ProfilePreview';
import { COLOR_THEMES } from '../../constants/themes';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';

const BK_CSS = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  .bk-title{font-family:'abcNormal','Inter','Arial',sans-serif;font-weight:300;letter-spacing:-0.01em;color:#0A0A0A;font-size:28px;margin:0;}
  .bk-subtitle{font-family:'Inter',system-ui,sans-serif;font-size:13px;color:#6B6B6B;margin-top:4px;letter-spacing:-0.01em;}
  .bk-section{background:#fff;border:1px solid #E5E5E5;border-radius:12px;padding:24px;}
  .bk-sec-title{font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;margin:0 0 16px;}
  .bk-banner{padding:10px 16px;border-radius:10px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:500;letter-spacing:-0.01em;border:1px solid;}
  .bk-banner--ok{background:rgba(52,199,89,0.08);border-color:rgba(52,199,89,0.2);color:#1a7f3c;}
  .bk-banner--error{background:rgba(255,59,48,0.08);border-color:rgba(255,59,48,0.2);color:#ff3b30;}
  .bk-form-label{display:block;font-size:12px;font-weight:500;color:#6B6B6B;margin-bottom:6px;letter-spacing:-0.01em;font-family:'Inter',system-ui,sans-serif;}
  .bk-color-input{width:44px;height:44px;border-radius:10px;border:1px solid #E5E5E5;cursor:pointer;padding:2px;flex-shrink:0;}
  .bk-text-input{flex:1;border:1px solid rgba(0,0,0,0.12);border-radius:9px;padding:9px 12px;font-size:14px;color:#0A0A0A;font-family:'Inter',system-ui,sans-serif;letter-spacing:-0.01em;background:#fff;outline:none;transition:border-color .15s,box-shadow .15s;box-sizing:border-box;}
  .bk-text-input:focus{border-color:#0071e3;box-shadow:0 0 0 3px rgba(0,113,227,0.12);}

  .bk-savebar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:13px 16px;border-radius:10px;border:1px solid #E5E5E5;background:#FAFAFA;font-family:'Inter',system-ui,sans-serif;transition:background .15s,border-color .15s;}
  .bk-savebar.is-dirty{border-color:rgba(0,113,227,0.35);background:rgba(0,113,227,0.06);}
  .bk-savebar-label{font-size:13px;color:#8A8A8E;font-weight:500;letter-spacing:-0.01em;}
  .bk-savebar.is-dirty .bk-savebar-label{color:#0071e3;}
  .bk-discard-btn{background:none;border:none;font-size:13px;font-weight:600;color:#6B6B6B;cursor:pointer;padding:8px 10px;font-family:'Inter',system-ui,sans-serif;}
  .bk-discard-btn:hover{color:#0A0A0A;}
  .bk-discard-btn:disabled{opacity:.5;cursor:not-allowed;}
  .bk-save-btn--bar{width:auto;padding:9px 18px;font-size:13px;}

  .bk-tpl-grid{display:flex;gap:10px;}
  .bk-tpl-btn{flex:1;text-align:left;padding:14px 16px;border:2px solid #E5E5E5;border-radius:10px;background:#fff;cursor:pointer;transition:border-color .15s,background .15s;display:flex;flex-direction:column;gap:4px;}
  .bk-tpl-btn:hover{border-color:#D4D4D4;}
  .bk-tpl-btn.on{border-color:#0A0A0A;background:#0A0A0A;}
  .bk-tpl-name{font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;}
  .bk-tpl-btn.on .bk-tpl-name{color:#fff;}
  .bk-tpl-desc{font-family:'Inter',system-ui,sans-serif;font-size:12px;color:#8A8A8E;letter-spacing:-0.01em;line-height:1.35;}
  .bk-tpl-btn.on .bk-tpl-desc{color:rgba(255,255,255,0.65);}

  .bk-theme-card{cursor:pointer;border:2px solid #E5E5E5;border-radius:10px;padding:16px;transition:border-color .15s,box-shadow .15s;}
  .bk-theme-card:hover{border-color:#D4D4D4;}
  .bk-theme-card.on{border-color:#0A0A0A;box-shadow:0 0 0 1px #0A0A0A;}
  .bk-theme-card--custom{border-style:dashed;}
  .bk-theme-card--custom.on{border-style:solid;}
  .bk-custom-colors{margin-top:20px;padding-top:20px;border-top:1px solid #E5E5E5;}
  .bk-tpl-note{background:#FAFAFA;border:1px solid #E5E5E5;border-radius:10px;padding:12px 14px;font-size:12.5px;color:#6B6B6B;line-height:1.5;font-family:'Inter',system-ui,sans-serif;letter-spacing:-0.01em;margin:0;}

  .bk-palette-box{background:#FAFAFA;border:1px solid #E5E5E5;border-radius:10px;padding:16px;}
  .bk-apply-btn{font-size:12px;font-weight:600;background:#0A0A0A;color:#fff;padding:5px 14px;border-radius:8px;border:none;cursor:pointer;transition:background .15s;font-family:'Inter',system-ui,sans-serif;}
  .bk-apply-btn:hover{background:#1a1a1a;}
  .bk-save-btn{background:#0A0A0A;color:#fff;border:none;border-radius:10px;padding:11px 18px;font-size:14px;font-weight:600;cursor:pointer;transition:opacity .15s;font-family:'Inter',system-ui,sans-serif;white-space:nowrap;}
  .bk-save-btn:hover{opacity:.86;}
  .bk-save-btn:disabled{opacity:.5;cursor:not-allowed;}

  .bk-hint{font-size:11.5px;color:#9A9A9A;margin-top:8px;font-family:'Inter',system-ui,sans-serif;}

  @media (max-width: 768px) {
    .bk-tpl-btn{padding:10px 12px;}
    .bk-tpl-name{font-size:13px;}
    .bk-tpl-desc{font-size:11px;}
    .bk-theme-card{padding:11px;}
    .bk-palette-box{padding:11px;border-radius:7px;}
    .bk-apply-btn{font-size:10.5px;padding:4px 9px;min-height:34px;}
    .bk-save-btn{padding:8px 12px;font-size:11px;min-height:34px;}
    .bk-save-btn--bar{min-height:34px;}
    .bk-savebar{padding:11px 13px;flex-wrap:wrap;}
    .bk-savebar-label{font-size:12px;}
    .bk-preview-frame{width:100%!important;max-width:300px;margin:0 auto;}
    .bk-section{padding:15px!important;}
    .bk-sec-title{font-size:13.5px!important;margin-bottom:12px!important;}
    .bk-mobile-controls{gap:14px!important;}
    .bk-mobile-preview-first{order:-1;position:static!important;}
    .bk-mobile-theme-grid{grid-template-columns:repeat(2,1fr)!important;}
    .bk-mobile-stack{gap:14px!important;}
  }

  /* Apply-to-profiles modal */
  .bk-modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,0.4);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:200;padding:20px;}
  @keyframes bk-modalIn{from{opacity:0;transform:scale(.96) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
  .bk-modal{background:#fff;border-radius:18px;box-shadow:0 24px 60px rgba(0,0,0,0.2);max-width:440px;width:100%;max-height:82vh;display:flex;flex-direction:column;overflow:hidden;animation:bk-modalIn .22s cubic-bezier(.22,1,.36,1) both;font-family:'Inter',system-ui,sans-serif;}
  .bk-modal-header{padding:22px 22px 14px;border-bottom:1px solid #F0F0F0;}
  .bk-modal-header h3{font-size:16px;font-weight:700;color:#0A0A0A;margin:0 0 8px;letter-spacing:-0.01em;}
  .bk-modal-sub{font-size:12.5px;color:#6B6B6B;line-height:1.55;margin:0;}
  .bk-modal-actions-row{display:flex;gap:14px;padding:12px 22px 0;}
  .bk-link-btn{background:none;border:none;padding:0;font-size:12.5px;font-weight:600;color:#0071e3;cursor:pointer;font-family:inherit;}
  .bk-link-btn:hover{text-decoration:underline;}
  .bk-modal-search{margin:12px 22px 0;border:1px solid #E5E5E5;border-radius:9px;padding:8px 12px;font-size:13px;font-family:inherit;outline:none;}
  .bk-modal-search:focus{border-color:#0071e3;}
  .bk-modal-list{overflow-y:auto;padding:12px 10px;flex:1;}
  .bk-modal-row{display:flex;align-items:center;gap:12px;padding:9px 12px;border-radius:10px;cursor:pointer;transition:background .12s;}
  .bk-modal-row:hover{background:#FAFAFA;}
  .bk-modal-row input[type="checkbox"]{width:16px;height:16px;flex-shrink:0;accent-color:#0A0A0A;cursor:pointer;}
  .bk-modal-avatar{width:34px;height:34px;border-radius:50%;object-fit:cover;flex-shrink:0;background:#F0F0F0;}
  .bk-modal-avatar--empty{display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:600;color:#8A8A8E;}
  .bk-modal-row-name{font-size:13.5px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;}
  .bk-modal-row-role{font-size:11.5px;color:#8A8A8E;margin-top:1px;}
  .bk-modal-footer{display:flex;align-items:center;justify-content:flex-end;gap:10px;padding:14px 22px;border-top:1px solid #F0F0F0;}

  /* Company logo */
  .bk-logo-row{display:flex;align-items:center;gap:16px;}
  .bk-logo-preview{width:72px;height:72px;border-radius:14px;background:#FAFAFA;border:1px solid #E5E5E5;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0;}
  .bk-logo-preview img{width:100%;height:100%;object-fit:contain;}
  .bk-logo-placeholder{font-size:11px;color:#9A9A9A;text-align:center;line-height:1.3;font-family:'Inter',system-ui,sans-serif;}
  .bk-logo-actions{display:flex;gap:10px;flex-wrap:wrap;}
  .bk-logo-upload-btn{background:#0A0A0A;color:#fff;border:none;border-radius:9px;padding:9px 16px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Inter',system-ui,sans-serif;transition:opacity .15s;}
  .bk-logo-upload-btn:hover{opacity:.88;}
  .bk-logo-upload-btn:disabled{opacity:.5;cursor:not-allowed;}
  .bk-logo-remove-btn{background:none;border:1px solid #E5E5E5;color:#6B6B6B;border-radius:9px;padding:9px 16px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Inter',system-ui,sans-serif;}
  .bk-logo-remove-btn:hover{color:#ff3b30;border-color:rgba(255,59,48,0.3);}
`;

// No real photo needed — ProfilePreview already falls back to its normal
// "no photo yet" placeholder, same as any real profile without one.
const SAMPLE_PROFILE = {
  name: 'Ana',
  lastname: 'Torres',
  position: 'Gerente Comercial',
  company: 'Tu Empresa',
  bio: 'Así se vería un perfil con la marca de tu empresa aplicada.',
  email: 'ana@tuempresa.cl',
  whatsapp: '+56911111111',
  linkedin: 'https://linkedin.com',
};

const themeName = (key, theme, t) => t(`theme.${key}`, theme.name);

const buildDraft = (kit) => ({
  template: kit?.template || 'classic',
  theme: kit?.theme_color || 'blue',
  background_color: kit?.background_color || '#ffffff',
  text_color: kit?.text_color || '#000000',
  button_color: kit?.button_color || '#7CDAF9',
});

const draftsEqual = (a, b) =>
  a.template === b.template &&
  a.theme === b.theme &&
  a.background_color === b.background_color &&
  a.text_color === b.text_color &&
  a.button_color === b.button_color;

const BrandKitEditor = () => {
  const { toast } = useNotification();
  const { t } = useLanguage();
  const [draft, setDraft] = useState(() => buildDraft(null));
  const [saved, setSaved] = useState(() => buildDraft(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [profileSearch, setProfileSearch] = useState('');
  // Whether a kit already existed on load — distinguishes the very first
  // save (default the picker to "all checked", a helpful assumption with
  // nothing to lose yet) from editing an existing kit (default the picker
  // to whatever's currently inheriting, so a color tweak doesn't silently
  // re-include profiles someone deliberately excluded in an earlier save).
  const [kitExisted, setKitExisted] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [kitRes, profilesRes] = await Promise.all([
          axios.get(`${API}/teams/brand-kit`),
          axios.get(`${API}/profiles/me`),
        ]);
        if (!mounted) return;
        const fetchedKit = kitRes.data?.brand_kit || null;
        setDraft(buildDraft(fetchedKit));
        setSaved(buildDraft(fetchedKit));
        setKitExisted(!!fetchedKit);
        setLogoUrl(fetchedKit?.company_logo_url || null);
        setProfiles(Array.isArray(profilesRes.data) ? profilesRes.data : []);
      } catch (err) {
        console.warn('[BrandKitEditor] failed to load brand kit', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('brandKit.logo.invalidType', 'El archivo debe ser una imagen'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('brandKit.logo.tooLarge', 'La imagen no puede superar los 5MB'));
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/teams/brand-kit/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(res.data?.company_logo_url || null);
      toast.success(t('brandKit.logo.uploaded', 'Logo actualizado'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('brandKit.logo.uploadError', 'No se pudo subir el logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    setUploadingLogo(true);
    try {
      await axios.delete(`${API}/teams/brand-kit/logo`);
      setLogoUrl(null);
      toast.success(t('brandKit.logo.removed', 'Logo eliminado'));
    } catch (err) {
      toast.error(t('brandKit.logo.removeError', 'No se pudo eliminar el logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const isDirty = !draftsEqual(draft, saved);

  // Same "don't lose unsaved changes on tab close" pattern as AppearanceEditor.
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const generatedPalette = useMemo(
    () => generateColorPalette(draft.button_color),
    [draft.button_color]
  );

  const previewColors = useMemo(() => {
    const palette = generateColorPalette(draft.button_color);
    return {
      background: draft.background_color,
      text: draft.text_color,
      button: draft.button_color,
      buttonAlt: palette?.secondary || draft.button_color,
      buttonText: palette?.textOnPrimary || getReadableTextColor(draft.button_color),
    };
  }, [draft]);

  const handleTemplateChange = (template) => setDraft((prev) => ({ ...prev, template }));

  const handleSelectTheme = (key) => {
    if (key === 'custom') {
      setDraft((prev) => ({ ...prev, theme: 'custom' }));
      return;
    }
    const theme = COLOR_THEMES[key];
    if (!theme) return;
    setDraft((prev) => ({
      ...prev,
      theme: key,
      background_color: theme.background,
      text_color: theme.text,
      button_color: theme.button,
    }));
  };

  const handleApplySuggestedColors = () => {
    if (!generatedPalette) return;
    setDraft((prev) => ({
      ...prev,
      background_color: generatedPalette.backgroundLight,
      text_color: generatedPalette.textOnLight,
    }));
  };

  const handleDiscard = () => setDraft(saved);

  const doSave = async (applyToProfileIds) => {
    setSaving(true);
    try {
      await axios.put(`${API}/teams/brand-kit`, {
        template: draft.template,
        theme_color: draft.theme,
        background_color: draft.background_color,
        text_color: draft.text_color,
        button_color: draft.button_color,
        apply_to_profile_ids: applyToProfileIds,
      });
      setSaved(draft);
      setKitExisted(true);
      // Keep local profile state in sync with what was just applied, so
      // reopening the picker later in the same session (without a full page
      // reload) still reflects the real current selection instead of the
      // stale state from the initial page load.
      if (applyToProfileIds !== null) {
        const appliedSet = new Set(applyToProfileIds);
        setProfiles((prev) => prev.map((p) => ({ ...p, hereda_marca: appliedSet.has(p.id) })));
      }
      setShowApplyModal(false);
      setMessage(t('brandKit.toast.saved', 'Marca de tu empresa guardada correctamente'));
      toast.success(t('brandKit.toast.savedShort', 'Marca de tu empresa guardada'));
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || t('brandKit.toast.saveError', 'No se pudo guardar la marca');
      setMessage(`Error: ${msg}`);
      toast.error(msg);
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleSaveClick = () => {
    // Nothing to pick from yet — save straight away instead of showing an
    // empty "apply to your profiles" picker that makes no sense with zero
    // profiles.
    if (profiles.length === 0) {
      doSave(null);
      return;
    }
    // First-ever kit: nothing to lose yet, default to "all checked" so most
    // people can just hit confirm. Editing an existing kit: default to
    // whichever profiles are ALREADY inheriting, so a color tweak doesn't
    // silently re-include profiles someone deliberately excluded before.
    const initialSelection = kitExisted
      ? profiles.filter((p) => !!p.hereda_marca).map((p) => p.id)
      : profiles.map((p) => p.id);
    setSelectedIds(new Set(initialSelection));
    setProfileSearch('');
    setShowApplyModal(true);
  };

  const toggleProfileSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredProfiles = profiles.filter((p) => {
    const q = profileSearch.trim().toLowerCase();
    if (!q) return true;
    const fullName = `${p.name || ''} ${p.lastname || ''}`.toLowerCase();
    return fullName.includes(q);
  });

  if (loading) {
    return (
      <div className="w-full dashboard-container">
        <style>{BK_CSS}</style>
        <div className="mb-8">
          <h1 className="bk-title">{t('brandKit.title', 'Marca de tu empresa')}</h1>
        </div>
        <p className="bk-subtitle">{t('brandKit.loading', 'Cargando...')}</p>
      </div>
    );
  }

  const messageType = message.startsWith('Error') ? 'error' : 'ok';

  return (
    <div className="w-full dashboard-container">
      <style>{BK_CSS}</style>
      <div className="mb-8">
        <h1 className="bk-title">{t('brandKit.title', 'Marca de tu empresa')}</h1>
        <p className="bk-subtitle">{t('brandKit.subtitle', 'Se aplicará a los perfiles nuevos de tu equipo.')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 bk-mobile-stack">
        {/* Panel izquierdo - Controles */}
        <div className="space-y-8 bk-mobile-controls">
          {message && (
            <div className={`bk-banner bk-banner--${messageType}`}>
              {message}
            </div>
          )}

          <div className={`bk-savebar ${isDirty ? 'is-dirty' : ''}`}>
            <span className="bk-savebar-label">
              {isDirty ? t('appearance.unsaved', 'Tienes cambios sin guardar') : t('appearance.allSaved', 'Todo guardado')}
            </span>
            <div className="flex gap-2">
              {isDirty && (
                <button className="bk-discard-btn" onClick={handleDiscard} disabled={saving}>
                  {t('appearance.discard', 'Descartar')}
                </button>
              )}
              <button
                className="bk-save-btn bk-save-btn--bar"
                onClick={handleSaveClick}
                disabled={!isDirty || saving}
              >
                {saving ? t('appearance.saving', 'Guardando...') : t('appearance.saveChanges', 'Guardar cambios')}
              </button>
            </div>
          </div>

          {/* Logo de la empresa */}
          <div className="bk-section">
            <h3 className="bk-sec-title">{t('brandKit.logo.title', 'Logo de la empresa')}</h3>
            <div className="bk-logo-row">
              <div className="bk-logo-preview">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" />
                ) : (
                  <span className="bk-logo-placeholder">{t('brandKit.logo.none', 'Sin logo')}</span>
                )}
              </div>
              <div className="bk-logo-actions">
                <label className="bk-logo-upload-btn" style={{ opacity: uploadingLogo ? 0.5 : 1 }}>
                  {uploadingLogo ? t('brandKit.logo.uploading', 'Subiendo...') : (logoUrl ? t('brandKit.logo.change', 'Cambiar logo') : t('brandKit.logo.upload', 'Subir logo'))}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    disabled={uploadingLogo}
                    onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                  />
                </label>
                {logoUrl && (
                  <button className="bk-logo-remove-btn" onClick={handleLogoDelete} disabled={uploadingLogo}>
                    {t('brandKit.logo.remove', 'Quitar')}
                  </button>
                )}
              </div>
            </div>
            <p className="bk-hint">{t('brandKit.logo.hint', 'Se muestra en el panel de Equipo y se aplica a los perfiles que heredan tu marca.')}</p>
          </div>

          {/* Plantilla */}
          <div className="bk-section">
            <h3 className="bk-sec-title">{t('appearance.template.title', 'Plantilla')}</h3>
            <div className="bk-tpl-grid">
              <button
                onClick={() => handleTemplateChange('classic')}
                className={`bk-tpl-btn ${draft.template === 'classic' ? 'on' : ''}`}
              >
                <span className="bk-tpl-name">{t('appearance.template.classic', 'Clásica')}</span>
                <span className="bk-tpl-desc">{t('brandKit.template.classicDesc', 'Fondo inmersivo con degradado del color principal')}</span>
              </button>

              <button
                onClick={() => handleTemplateChange('corporate')}
                className={`bk-tpl-btn ${draft.template === 'corporate' ? 'on' : ''}`}
              >
                <span className="bk-tpl-name">{t('appearance.template.corporate', 'Corporativa')}</span>
                <span className="bk-tpl-desc">{t('appearance.template.corporateDesc', 'Fondo sólido y banner, con colores de fondo y texto propios')}</span>
              </button>

              <button
                onClick={() => handleTemplateChange('portrait')}
                className={`bk-tpl-btn ${draft.template === 'portrait' ? 'on' : ''}`}
              >
                <span className="bk-tpl-name">{t('appearance.template.portrait', 'Retrato')}</span>
                <span className="bk-tpl-desc">{t('brandKit.template.portraitDesc', 'Foto a página completa, con un panel de color debajo')}</span>
              </button>
            </div>
          </div>

          {/* Color: temas predefinidos + personalizado, unificados */}
          <div className="bk-section">
            <h3 className="bk-sec-title">{t('appearance.color.title', 'Color')}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 bk-mobile-theme-grid">
              {Object.entries(COLOR_THEMES).map(([key, theme]) => (
                <div
                  key={key}
                  onClick={() => handleSelectTheme(key)}
                  className={`bk-theme-card ${draft.theme === key ? 'on' : ''}`}
                  style={{ backgroundColor: theme.background }}
                >
                  <div className="text-center">
                    <div
                      className="w-12 h-12 rounded-full mx-auto mb-3"
                      style={{ backgroundColor: theme.button }}
                    ></div>
                    <h4 className="font-medium" style={{ color: theme.text }}>
                      {themeName(key, theme, t)}
                    </h4>
                    <div className="flex justify-center space-x-1 mt-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: theme.background }}></div>
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: theme.text }}></div>
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: theme.button }}></div>
                    </div>
                  </div>
                </div>
              ))}

              <div
                onClick={() => handleSelectTheme('custom')}
                className={`bk-theme-card bk-theme-card--custom ${draft.theme === 'custom' ? 'on' : ''}`}
              >
                <div className="text-center">
                  <div
                    className="w-12 h-12 rounded-full mx-auto mb-3"
                    style={{ background: 'conic-gradient(from 0deg, #3b82f6, #10b981, #f59e0b, #ef4444, #8b5cf6, #3b82f6)' }}
                  ></div>
                  <h4 className="font-medium" style={{ color: '#0A0A0A' }}>{t('appearance.color.custom', 'Personalizado')}</h4>
                  <p style={{ fontSize: 11, color: '#9A9A9A', marginTop: 6, fontFamily: "'Inter',system-ui,sans-serif" }}>
                    {t('appearance.color.customDesc', 'Elige tus propios colores')}
                  </p>
                </div>
              </div>
            </div>

            {draft.theme === 'custom' && (
              <div className="bk-custom-colors">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="bk-form-label">{t('appearance.color.mainButtonColor', 'Color Principal de Botones')}</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={draft.button_color}
                        onChange={(e) => setDraft((prev) => ({ ...prev, button_color: e.target.value }))}
                        className="bk-color-input"
                      />
                      <input
                        type="text"
                        value={draft.button_color}
                        onChange={(e) => setDraft((prev) => ({ ...prev, button_color: e.target.value }))}
                        className="bk-text-input"
                        placeholder="#7CDAF9"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{t('appearance.color.mainButtonColorHint', 'Este color genera automáticamente el resto de la paleta')}</p>
                  </div>

                  {generatedPalette && (
                    <div className="bk-palette-box">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-700">{t('appearance.color.paletteTitle', 'Paleta generada automáticamente')}</h4>
                        <button onClick={handleApplySuggestedColors} className="bk-apply-btn">
                          {t('appearance.color.applySuggestions', 'Aplicar sugerencias')}
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="text-center">
                          <div className="w-full h-12 rounded-lg border border-gray-200 mb-1" style={{ backgroundColor: generatedPalette.primary }}></div>
                          <p className="text-xs text-gray-600">{t('appearance.color.primary', 'Principal')}</p>
                        </div>
                        <div className="text-center">
                          <div className="w-full h-12 rounded-lg border border-gray-200 mb-1" style={{ backgroundColor: generatedPalette.secondary }}></div>
                          <p className="text-xs text-gray-600">{t('appearance.color.secondary', 'Secundario')}</p>
                        </div>
                        <div className="text-center">
                          <div className="w-full h-12 rounded-lg border border-gray-200 mb-1" style={{ backgroundColor: generatedPalette.hover }}></div>
                          <p className="text-xs text-gray-600">Hover</p>
                        </div>
                        <div className="text-center">
                          <div className="w-full h-12 rounded-lg border border-gray-200 mb-1" style={{ backgroundColor: generatedPalette.accent }}></div>
                          <p className="text-xs text-gray-600">{t('appearance.color.accent', 'Acento')}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {draft.template === 'corporate' || draft.template === 'portrait' ? (
                    <>
                      <div>
                        <label className="bk-form-label">{t('appearance.color.backgroundColor', 'Color de Fondo')}</label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            value={draft.background_color}
                            onChange={(e) => setDraft((prev) => ({ ...prev, background_color: e.target.value }))}
                            className="bk-color-input"
                          />
                          <input
                            type="text"
                            value={draft.background_color}
                            onChange={(e) => setDraft((prev) => ({ ...prev, background_color: e.target.value }))}
                            className="bk-text-input"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="bk-form-label">{t('appearance.color.textColor', 'Color de Texto')}</label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            value={draft.text_color}
                            onChange={(e) => setDraft((prev) => ({ ...prev, text_color: e.target.value }))}
                            className="bk-color-input"
                          />
                          <input
                            type="text"
                            value={draft.text_color}
                            onChange={(e) => setDraft((prev) => ({ ...prev, text_color: e.target.value }))}
                            className="bk-text-input"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="bk-tpl-note">
                      {t('brandKit.color.classicNote', 'La plantilla Clásica usa un degradado automático a partir del color principal. El fondo y el texto no son personalizables en este modo.')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Panel derecho - Vista previa en tiempo real */}
        <div className="lg:sticky lg:top-8 lg:self-start bk-mobile-preview-first">
          <div className="bk-section">
            <h3 className="bk-sec-title">{t('appearance.preview.title', 'Vista previa')}</h3>
            <div className="flex justify-center">
              <div className="bk-preview-frame rounded-2xl border border-gray-200 overflow-hidden" style={{ width: 375 }}>
                <ProfilePreview
                  profile={SAMPLE_PROFILE}
                  template={draft.template}
                  colors={previewColors}
                  isPreview={true}
                  showWatermark={false}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 text-center mt-3">
              {t('brandKit.preview.desc', 'Así se vería un perfil con esta marca aplicada.')}
            </p>
          </div>
        </div>
      </div>

      {showApplyModal && (
        <div className="bk-modal-backdrop" onClick={() => !saving && setShowApplyModal(false)}>
          <div className="bk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bk-modal-header">
              <h3>{t('brandKit.applyModal.title', '¿A qué perfiles aplicar tu marca?')}</h3>
              <p className="bk-modal-sub">
                {t('brandKit.applyModal.desc', 'Esto reemplaza la plantilla y los colores actuales de los perfiles que dejes marcados. No se puede deshacer automáticamente.')}
              </p>
            </div>
            <div className="bk-modal-actions-row">
              <button className="bk-link-btn" onClick={() => setSelectedIds(new Set(profiles.map((p) => p.id)))}>
                {t('brandKit.applyModal.selectAll', 'Seleccionar todos')}
              </button>
              <button className="bk-link-btn" onClick={() => setSelectedIds(new Set())}>
                {t('brandKit.applyModal.deselectAll', 'Quitar selección')}
              </button>
            </div>
            {profiles.length > 6 && (
              <input
                className="bk-modal-search"
                placeholder={t('brandKit.applyModal.searchPlaceholder', 'Buscar perfil...')}
                value={profileSearch}
                onChange={(e) => setProfileSearch(e.target.value)}
              />
            )}
            <div className="bk-modal-list">
              {filteredProfiles.map((p) => (
                <label key={p.id} className="bk-modal-row">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(p.id)}
                    onChange={() => toggleProfileSelected(p.id)}
                  />
                  {p.profile_image_url ? (
                    <img src={p.profile_image_url} alt={p.name} className="bk-modal-avatar" />
                  ) : (
                    <div className="bk-modal-avatar bk-modal-avatar--empty">
                      {(p.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="bk-modal-row-name">{p.name}{p.lastname ? ` ${p.lastname}` : ''}</div>
                    <div className="bk-modal-row-role">{p.position || t('teams.members.noRole', 'Sin cargo')}</div>
                  </div>
                </label>
              ))}
              {filteredProfiles.length === 0 && (
                <p className="bk-hint" style={{ padding: '8px 12px' }}>{t('brandKit.applyModal.noMatches', 'Ningún perfil coincide con la búsqueda.')}</p>
              )}
            </div>
            <div className="bk-modal-footer">
              <button className="bk-discard-btn" onClick={() => setShowApplyModal(false)} disabled={saving}>
                {t('brandKit.applyModal.cancel', 'Cancelar')}
              </button>
              <button className="bk-save-btn" onClick={() => doSave(Array.from(selectedIds))} disabled={saving}>
                {saving ? t('brandKit.applyModal.applying', 'Aplicando...') : t('brandKit.applyModal.applyTo', `Aplicar a ${selectedIds.size} de ${profiles.length} perfiles`).replace('{selected}', selectedIds.size).replace('{total}', profiles.length)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandKitEditor;
